import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ArrowRightIcon, MapPinIcon } from "@heroicons/react/24/outline";

export default function PopularDestinations({ limit = 8 }) {
    const [destinations, setDestinations] = useState([]);
    const [loading, setLoading] = useState(true);

    const navigate = useNavigate();

    // =========================================================
    // FETCH DESTINATIONS
    // =========================================================

    useEffect(() => {
        const fetchDestinations = async () => {
            try {
                const res = await axios.get(
                    "https://marinadubai.com/api/destinations"
                );

                setDestinations(res.data);
            } catch (err) {
                console.error(
                    "Failed to load destinations:",
                    err
                );
            } finally {
                setLoading(false);
            }
        };

        fetchDestinations();
    }, []);

    // =========================================================
    // LOADING SKELETON
    // =========================================================

    if (loading) {
        return (
            <section
                className="
                    w-full
                    bg-[#F7F5EF]
                    py-12
                    sm:py-14
                    lg:py-16
                "
            >
                <div
                    className="
                        mx-auto
                        max-w-[1310px]
                    "
                >
                    <div
                        className="
                            grid
                            grid-cols-1
                            gap-8

                            lg:grid-cols-[220px_1fr]
                            lg:items-center
                        "
                    >
                        {/* Left skeleton */}

                        <div className="space-y-4">
                            <div className="h-3 w-24 animate-pulse rounded bg-[#D9D5CA]" />

                            <div className="h-10 w-44 animate-pulse rounded bg-[#D9D5CA]" />

                            <div className="h-10 w-40 animate-pulse rounded bg-[#D9D5CA]" />

                            <div className="h-4 w-48 animate-pulse rounded bg-[#E0DDD4]" />

                            <div className="h-10 w-36 animate-pulse rounded-full bg-[#D9D5CA]" />
                        </div>

                        {/* Cards skeleton */}

                        <div
                            className="
                                grid
                                grid-cols-2
                                gap-3

                                sm:grid-cols-2
                                sm:gap-4

                                xl:grid-cols-4
                            "
                        >
                            {[1, 2, 3, 4].map((item) => (
                                <div
                                    key={item}
                                    className="
                                        h-[220px]
                                        animate-pulse
                                        rounded-[10px]
                                        bg-[#DDD9CF]

                                        sm:h-[245px]
                                    "
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    // =========================================================
    // RENDER
    // =========================================================

    return (
        <section
            className="
                relative
                w-full
                overflow-hidden
                bg-[#F7F5EF]

                py-12
                sm:py-14
                lg:py-16
            "
        >
            {/* =====================================================
                SOFT BACKGROUND DECORATION
            ===================================================== */}

            <div
                className="
                    pointer-events-none
                    absolute
                    -left-32
                    top-20
                    h-72
                    w-72
                    rounded-full
                    bg-[#E9E2D2]
                    opacity-40
                    blur-3xl
                "
            />

            <div
                className="
                    pointer-events-none
                    absolute
                    -right-32
                    bottom-0
                    h-80
                    w-80
                    rounded-full
                    bg-[#E4D6B5]
                    opacity-30
                    blur-3xl
                "
            />

            {/* =====================================================
                MAIN CONTAINER
            ===================================================== */}

            <div
                className="
                    relative
                    z-10
                    mx-auto
                    max-w-[1310px]

                "
            >
                <div
                    className="
                        grid
                        grid-cols-1
                        gap-8

                        lg:grid-cols-[220px_minmax(0,1fr)]
                        lg:items-center
                        lg:gap-7

                        xl:grid-cols-[220px_minmax(0,1fr)]
                        xl:gap-8
                    "
                >

                    {/* =================================================
                        LEFT CONTENT
                    ================================================= */}

                    <div
                        className="
                            flex
                            flex-col
                            items-start
                        "
                    >
                        {/* Small gold line + label */}

                        <div
                            className="
                                mb-3
                                flex
                                items-center
                                gap-2
                            "
                        >
                            <span
                                className="
                                    h-[2px]
                                    w-6
                                    rounded-full
                                    bg-[#D5A33C]
                                "
                            />

                            <span
                                className="
                                    text-[9px]
                                    font-semibold
                                    uppercase
                                    tracking-[0.19em]
                                    text-[#315C5A]
                                "
                            >
                                Popular Destinations
                            </span>
                        </div>

                        {/* Heading */}

                        <h2
                            className="
                                max-w-[230px]

                                font-serif
                                text-[28px]
                                font-medium
                                leading-[1.12]
                                tracking-[-0.02em]

                                text-[#073F3D]

                                sm:text-[30px]

                                lg:text-[29px]

                                xl:text-[31px]
                            "
                        >
                            Explore Dubai's
                            <br />
                            Most Loved
                            <br />
                            Places
                        </h2>

                        {/* Description */}

                        <p
                            className="
                                mt-3
                                max-w-[215px]

                                text-[10px]
                                leading-[1.65]

                                text-[#5D716F]
                            "
                        >
                            From iconic landmarks to stunning
                            beaches, find the perfect destination
                            for your next getaway.
                        </p>

                        {/* View all button */}

                        <button
                            type="button"
                            onClick={() =>
                                navigate("/destinations")
                            }
                            className="
                                group
                                mt-5

                                inline-flex
                                items-center
                                gap-2.5

                                rounded-full

                                bg-[#073F3D]

                                px-5
                                py-2.5

                                text-[10px]
                                font-semibold

                                text-white

                                shadow-[0_5px_15px_rgba(7,63,61,0.14)]

                                transition-all
                                duration-300

                                hover:-translate-y-0.5
                                hover:bg-[#0A504D]
                                hover:shadow-[0_8px_20px_rgba(7,63,61,0.18)]
                            "
                        >
                            <span>
                                Explore All Destinations
                            </span>

                            <ArrowRightIcon
                                className="
                                    h-3.5
                                    w-3.5

                                    transition-transform
                                    duration-300

                                    group-hover:translate-x-1
                                "
                            />
                        </button>
                    </div>

                    {/* =================================================
                        DESTINATION CARDS
                    ================================================= */}

                    <div
                        className="
                            grid
                            grid-cols-2
                            gap-3

                            sm:gap-4

                            lg:grid-cols-4
                            lg:gap-3.5

                            xl:gap-4
                        "
                    >
                        {destinations
                            .slice(0, limit)
                            .map((dest, index) => (
                                <div
                                    key={dest.id}
                                    onClick={() =>
                                        navigate(
                                            `/destinations/${dest.slug}`
                                        )
                                    }
                                    className="
                                        destination-card
                                        group
                                        relative

                                        h-[205px]

                                        cursor-pointer
                                        overflow-hidden
                                        rounded-[10px]

                                        bg-[#123E3C]

                                        shadow-[0_5px_18px_rgba(7,63,61,0.10)]

                                        transition-all
                                        duration-500

                                        hover:-translate-y-1
                                        hover:shadow-[0_14px_30px_rgba(7,63,61,0.18)]

                                        sm:h-[225px]

                                        lg:h-[230px]

                                        xl:h-[245px]
                                    "
                                    style={{
                                        animation: `destinationFadeUp 0.55s ease-out ${index * 80
                                            }ms both`,
                                    }}
                                >
                                    {/* =================================================
                                        IMAGE
                                    ================================================= */}

                                    <img
                                        src={`https://uaediscountedhotels.com/uploads/destinations/${dest.image}`}
                                        alt={dest.name}
                                        className="
                                            absolute
                                            inset-0

                                            h-full
                                            w-full

                                            object-cover

                                            transition-transform
                                            duration-700
                                            ease-out

                                            group-hover:scale-110
                                        "
                                    />

                                    {/* =================================================
                                        IMAGE OVERLAY
                                    ================================================= */}

                                    <div
                                        className="
                                            absolute
                                            inset-0

                                            bg-gradient-to-t
                                            from-[#032E2D]/95
                                            via-[#073F3D]/20
                                            to-transparent
                                        "
                                    />

                                    {/* Subtle hover tint */}

                                    <div
                                        className="
                                            absolute
                                            inset-0

                                            bg-[#073F3D]/0

                                            transition-colors
                                            duration-500

                                            group-hover:bg-[#073F3D]/15
                                        "
                                    />

                                    {/* =================================================
                                        TOP HOTEL COUNT
                                    ================================================= */}

                                    <div
                                        className="
                                            absolute
                                            right-3
                                            top-3
                                        "
                                    >
                                        <span
                                            className="
                                                inline-flex
                                                items-center

                                                rounded-full

                                                border
                                                border-white/20

                                                bg-[#073F3D]/65

                                                px-2.5
                                                py-1

                                                text-[8px]
                                                font-medium

                                                text-white

                                                backdrop-blur-md
                                            "
                                        >
                                            {dest.hotels_count} Hotels
                                        </span>
                                    </div>

                                    {/* =================================================
                                        BOTTOM CONTENT
                                    ================================================= */}

                                    <div
                                        className="
                                            absolute
                                            bottom-0
                                            left-0
                                            right-0

                                            p-3

                                            sm:p-3.5
                                        "
                                    >
                                        {/* Location */}

                                        <div
                                            className="
                                                mb-1.5

                                                flex
                                                items-center
                                                gap-1
                                            "
                                        >
                                            <MapPinIcon
                                                className="
                                                    h-3
                                                    w-3

                                                    text-[#E5C56D]
                                                "
                                                strokeWidth={2}
                                            />

                                            <span
                                                className="
                                                    text-[8px]
                                                    font-medium
                                                    uppercase
                                                    tracking-[0.12em]

                                                    text-white/75
                                                "
                                            >
                                                Dubai
                                            </span>
                                        </div>

                                        {/* Name + Arrow */}

                                        <div
                                            className="
                                                flex
                                                items-end
                                                justify-between
                                                gap-2
                                            "
                                        >
                                            <div
                                                className="
                                                    min-w-0
                                                "
                                            >
                                                <h3
                                                    className="
                                                        truncate

                                                        font-serif
                                                        text-[16px]
                                                        font-medium
                                                        leading-tight

                                                        text-white

                                                        sm:text-[17px]
                                                    "
                                                >
                                                    {dest.name}
                                                </h3>

                                                <p
                                                    className="
                                                        mt-1

                                                        text-[8px]

                                                        text-white/65
                                                    "
                                                >
                                                    {dest.hotels_count}
                                                    + hotels available
                                                </p>
                                            </div>

                                            {/* Arrow */}

                                            <div
                                                className="
                                                    flex
                                                    h-7
                                                    w-7
                                                    shrink-0

                                                    items-center
                                                    justify-center

                                                    rounded-full

                                                    border
                                                    border-white/20

                                                    bg-white/10

                                                    text-white

                                                    backdrop-blur-md

                                                    transition-all
                                                    duration-300

                                                    group-hover:translate-x-1
                                                    group-hover:border-[#E5C56D]
                                                    group-hover:bg-[#D5A33C]
                                                    group-hover:text-[#073F3D]
                                                "
                                            >
                                                <ArrowRightIcon
                                                    className="
                                                        h-3.5
                                                        w-3.5
                                                    "
                                                    strokeWidth={2}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* =================================================
                                        GOLD BOTTOM ACCENT
                                    ================================================= */}

                                    <div
                                        className="
                                            absolute
                                            bottom-0
                                            left-0

                                            h-[3px]
                                            w-0

                                            bg-[#D5A33C]

                                            transition-all
                                            duration-500

                                            group-hover:w-full
                                        "
                                    />
                                </div>
                            ))}
                    </div>
                </div>

                {/* =====================================================
                    MOBILE VIEW ALL
                ===================================================== */}

                <div className="mt-6 sm:hidden">
                    <button
                        type="button"
                        onClick={() =>
                            navigate("/destinations")
                        }
                        className="
                            group

                            flex
                            w-full
                            items-center
                            justify-center
                            gap-2

                            rounded-full

                            border
                            border-[#073F3D]

                            bg-transparent

                            px-5
                            py-3

                            text-[11px]
                            font-semibold

                            text-[#073F3D]

                            transition-all
                            duration-300

                            hover:bg-[#073F3D]
                            hover:text-white
                        "
                    >
                        <span>
                            View All Destinations
                        </span>

                        <ArrowRightIcon
                            className="
                                h-3.5
                                w-3.5

                                transition-transform
                                duration-300

                                group-hover:translate-x-1
                            "
                        />
                    </button>
                </div>
            </div>

            {/* =====================================================
                CARD ANIMATION
            ===================================================== */}

            <style>
                {`
                    @keyframes destinationFadeUp {
                        from {
                            opacity: 0;
                            transform: translateY(14px);
                        }

                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }

                    @media (prefers-reduced-motion: reduce) {
                        .destination-card {
                            animation: none !important;
                            transition: none !important;
                        }
                    }
                `}
            </style>
        </section>
    );
}